import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";
import { useRecentlyViewed } from "@/contexts/RecentlyViewedContext";
import ProductCard from "@/components/customer/ProductCard";
import PincodeDialog from "@/components/customer/PincodeDialog";
import TreeRenderer from "@/components/customer/TreeRenderer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Truck, ShieldCheck, Timer, Sparkles, ArrowRight, Package, Percent,
  ChevronLeft, ChevronRight, Flame, Tag, ShoppingBag, MapPin, Zap,
} from "lucide-react";

const CATEGORY_TILES = [
  { key: "atta",   label: "Atta, Rice & Dal", emoji: "🌾", bg: "bg-[hsl(40_90%_92%)]" },
  { key: "milk",   label: "Milk & Dairy",     emoji: "🥛", bg: "bg-[hsl(200_80%_94%)]" },
  { key: "snacks", label: "Snacks",           emoji: "🍿", bg: "bg-[hsl(20_87%_92%)]" },
  { key: "care",   label: "Personal Care",    emoji: "🧴", bg: "bg-[hsl(340_70%_94%)]" },
  { key: "cold",   label: "Cold Drinks",      emoji: "🥤", bg: "bg-[hsl(200_87%_94%)]" },
  { key: "clean",  label: "Household",        emoji: "🧼", bg: "bg-[hsl(280_50%_94%)]" },
  { key: "fresh",  label: "Fruits & Veg",     emoji: "🥬", bg: "bg-[hsl(80_60%_92%)]" },
  { key: "baby",   label: "Baby Care",        emoji: "🍼", bg: "bg-[hsl(20_87%_94%)]" },
];

function HeroSlider({ banners }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const paused = useRef(false);

  useEffect(() => {
    if (banners.length <= 1) return;
    const tick = () => { if (!paused.current) setIdx((i) => (i + 1) % banners.length); };
    timerRef.current = setInterval(tick, 4500);
    return () => clearInterval(timerRef.current);
  }, [banners.length]);

  if (banners.length === 0) return null;
  const go = (n) => setIdx((n + banners.length) % banners.length);

  return (
    <section
      className="max-w-7xl mx-auto px-3 md:px-6 pt-3"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      data-testid="home-slider"
    >
      <div className="relative rounded-2xl overflow-hidden aspect-[16/7] md:aspect-[24/6] bg-secondary">
        {banners.map((b, i) => (
          <Link
            key={i}
            to={b.link_to || "/shop"}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            data-testid={`home-slider-slide-${i}`}
          >
            <img src={b.image} alt={b.title || ""} className="w-full h-full object-cover" />
            {(b.title || b.subtitle) && (
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/25 to-transparent flex flex-col justify-end p-5 md:p-10 text-white">
                {b.title && <div className="font-display text-2xl md:text-4xl font-semibold max-w-md leading-tight">{b.title}</div>}
                {b.subtitle && <div className="text-sm md:text-base opacity-90 mt-1 max-w-lg">{b.subtitle}</div>}
                <div className="mt-4"><span className="inline-flex items-center gap-1 rounded-full bg-white text-primary px-4 py-1.5 text-sm font-semibold">Shop now <ArrowRight className="w-4 h-4" /></span></div>
              </div>
            )}
          </Link>
        ))}

        {banners.length > 1 && (
          <>
            <button onClick={() => go(idx - 1)} className="hidden md:grid absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 place-items-center rounded-full bg-white/85 hover:bg-white shadow" aria-label="Previous" data-testid="slider-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => go(idx + 1)} className="hidden md:grid absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 place-items-center rounded-full bg-white/85 hover:bg-white shadow" aria-label="Next" data-testid="slider-next">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  className={`slider-dot ${i === idx ? "active" : ""}`}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`slider-dot-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function DefaultHeroBanner({ user }) {
  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-3" data-testid="hero-default">
      <div className="relative rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-[hsl(var(--brand-red-deep))]" />
        <div className="absolute inset-0 opacity-[0.10]" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, white 2px, transparent 2px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)",
          backgroundSize: "60px 60px, 40px 40px",
        }} />
        <div className="relative px-5 py-6 md:px-10 md:py-10 text-primary-foreground grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold">
              <Sparkles className="w-3 h-3" /> Fresh from your Babu Store
            </div>
            <h1 className="mt-2.5 text-2xl md:text-4xl font-display font-semibold leading-[1.05] max-w-lg">
              Groceries in <span className="italic text-[hsl(20_87%_82%)]">minutes</span>, not hours.
            </h1>
            <p className="mt-2 text-primary-foreground/85 max-w-md text-xs md:text-sm">
              Atta, milk, snacks, personal care — delivered from Babu Store to your doorstep.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button asChild size="sm" variant="secondary" className="bg-white text-primary hover:bg-white/90 h-9 rounded-lg" data-testid="hero-shop-btn">
                <Link to="/shop">Shop now <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
              </Button>
              {!user && (
                <Button asChild size="sm" variant="outline" className="bg-white/15 backdrop-blur border-white/30 text-primary-foreground hover:bg-white/25 h-9 rounded-lg" data-testid="hero-signup-btn">
                  <Link to="/login">Create free account</Link>
                </Button>
              )}
            </div>
          </div>
          <div className="hidden md:grid w-36 h-36 rounded-2xl bg-white/10 backdrop-blur border border-white/20 place-items-center">
            <Package className="w-16 h-16 text-white/70" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = [
    { icon: Timer, label: "15-min delivery", sub: "In your locality" },
    { icon: Truck, label: "Free above ₹500", sub: "No hidden fees" },
    { icon: ShieldCheck, label: "GST invoices", sub: "On every order" },
    { icon: Tag, label: "Best local prices", sub: "Neighbourhood rates" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map((it) => (
          <div key={it.label} className="rounded-xl border bg-card px-3 py-2 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><it.icon className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold leading-tight truncate">{it.label}</div>
              <div className="text-[10px] text-muted-foreground truncate">{it.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const TILE_BGS = [
  "bg-[hsl(40_90%_92%)]",  "bg-[hsl(200_80%_94%)]",
  "bg-[hsl(20_87%_92%)]",  "bg-[hsl(340_70%_94%)]",
  "bg-[hsl(200_87%_94%)]", "bg-[hsl(280_50%_94%)]",
  "bg-[hsl(80_60%_92%)]",  "bg-[hsl(20_87%_94%)]",
  "bg-[hsl(160_50%_92%)]", "bg-[hsl(240_50%_94%)]",
];

/** Smart emoji fallback so category tiles never render a bland "📦" box.
 *  Matches by name keyword; falls back to shopping bag. */
const emojiFor = (name = "") => {
  const s = String(name).toLowerCase();
  if (/(atta|rice|dal|flour|chawal|grain|pulse|wheat)/.test(s)) return "🌾";
  if (/(milk|dairy|curd|paneer|butter|cheese|yogurt|ghee)/.test(s)) return "🥛";
  if (/(bread|bakery|cake|cookie|biscuit|rusk)/.test(s)) return "🍞";
  if (/(snack|chips|namkeen|bhujia)/.test(s)) return "🍿";
  if (/(fruit|vegetable|veg|fresh|greens)/.test(s)) return "🥬";
  if (/(cold|drink|juice|soda|beverage|water)/.test(s)) return "🥤";
  if (/(baby|diaper|infant)/.test(s)) return "🍼";
  if (/(care|beauty|cosmetic|skin|hair|personal)/.test(s)) return "🧴";
  if (/(clean|house|household|detergent|soap|wash)/.test(s)) return "🧼";
  if (/(sweet|chocolate|candy|dessert|mithai)/.test(s)) return "🍫";
  if (/(spice|masala|salt|sugar)/.test(s)) return "🧂";
  if (/(oil|ghee)/.test(s)) return "🫒";
  if (/(frozen|ice)/.test(s)) return "🧊";
  if (/(instant|noodle|pasta|ready)/.test(s)) return "🍜";
  if (/(tea|coffee)/.test(s)) return "☕";
  if (/(egg|meat|chicken|fish|non.?veg)/.test(s)) return "🥚";
  if (/(grocery|kitchen|essential|staple)/.test(s)) return "🛒";
  if (/(pet)/.test(s)) return "🐾";
  if (/(stationery|book|office)/.test(s)) return "📚";
  return "🛍️";
};

function DeliveryStrip() {
  const { etaMinutes, pincode, isServiceable, isOpen, closedReason } = useStore();
  const [pinOpen, setPinOpen] = useState(false);
  const bad = !isOpen || !isServiceable;
  return (
    <>
      <section className="max-w-7xl mx-auto px-3 md:px-6 pt-3 md:hidden" data-testid="home-delivery-strip">
        <button
          onClick={() => setPinOpen(true)}
          className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 border transition text-left ${bad ? "bg-destructive/5 border-destructive/30" : "bg-gradient-to-r from-primary/5 to-accent/10 border-primary/20"}`}
          data-testid="home-delivery-btn"
        >
          <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${bad ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`}>
            <Zap className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 text-sm font-semibold leading-tight">
              {bad ? (closedReason || "Delivery unavailable") : (
                <>Delivery in <span className="text-primary">{etaMinutes || 15} minutes</span></>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
              <MapPin className="w-3 h-3" />
              {pincode ? <>Home · <span className="font-medium">{pincode}</span></> : "Tap to set your delivery location"}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      </section>
      <PincodeDialog open={pinOpen} onOpenChange={setPinOpen} />
    </>
  );
}

function CategoryRail({ cats }) {
  // Always show the admin-configured categories. Fall back to hardcoded tiles
  // ONLY when the store has literally zero live categories (fresh install).
  const list = cats.length > 0 ? cats : CATEGORY_TILES;
  const { categoryStyle, categoryFont, categoryLayout, storefront = {} } = useStore();
  const railRef = useRef(null);
  // #13 Category card shape — admin-controlled: circle / rounded / square
  const shape =
    categoryStyle === "square"  ? "rounded-md" :
    categoryStyle === "rounded" ? "rounded-2xl" :
    /* circle */                  "rounded-full";
  // Sprint 28 — admin-controlled label typography
  const fontCls =
    categoryFont === "display"   ? "font-display italic" :
    categoryFont === "semibold"  ? "font-bold" :
    categoryFont === "condensed" ? "font-semibold tracking-tighter" :
    /* default */                  "font-medium";

  // Sprint — Advanced Category Customization. All admin-controlled from
  // UI Customization → Category. Numeric values fall back to sane defaults
  // so unset fields keep the original responsive Blinkit-style look.
  const boxSize     = Number(storefront.category_size)          || 0;      // px — 0 = default (62 mobile / grid-auto desktop)
  const imgPct      = Number(storefront.category_image_size)    || 100;    // % of box
  const imgFit      = storefront.category_image_fit             || "cover";// cover|contain|fill|none
  const boxPad      = Number(storefront.category_box_padding)   || 0;      // px
  const bWidth      = Number(storefront.category_border_width)  || 0;      // px
  const bColor      = storefront.category_border_color          || "";
  const boxBg       = storefront.category_bg                    || "";
  const imgBg       = storefront.category_image_bg              || "";
  const labelWeight = Number(storefront.category_label_weight)  || 0;
  const labelSizePx = Number(storefront.category_label_size)    || 0;
  const labelColor  = storefront.category_label_color           || "";
  const radiusPx    = Number(storefront.category_radius)        || 0;
  const gapPx       = Number(storefront.category_gap)           || 0;
  const shadowMode  = storefront.category_shadow                || "hover"; // none|hover|soft|medium|strong
  const shadowCls =
    shadowMode === "none"   ? "" :
    shadowMode === "soft"   ? "shadow-sm" :
    shadowMode === "medium" ? "shadow-md" :
    shadowMode === "strong" ? "shadow-lg" :
    /* hover (default) */      "group-hover:shadow-md";

  // Sprint — Category Layout (restored). Admin-controlled from
  // UI Customization → Category → Layout. Per-row values (desktop/mobile)
  // override Tailwind's static grid classes when grid/auto layout is active.
  const layout = categoryLayout || "auto";
  const [isDesktopVP, setIsDesktopVP] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(min-width: 768px)").matches
      : true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktopVP(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  const perRowDesktop = Number(storefront.category_per_row_desktop) || 10;
  const perRowMobile  = Number(storefront.category_per_row_mobile)  || 4;
  const perRow = isDesktopVP ? perRowDesktop : perRowMobile;

  const railStyle = {};
  const railCls =
    layout === "horizontal" ? "snap-rail rail-fade-mask pb-1" :
    layout === "vertical"   ? "grid grid-cols-1 pb-1" :
    layout === "grid"       ? "grid pb-1" :
    layout === "carousel"   ? "snap-rail rail-fade-mask pb-1" :
    /* auto */                "snap-rail rail-fade-mask md:mask-none md:grid pb-1";
  // For grid + auto (desktop grid), apply dynamic per-row via inline style.
  if (layout === "grid" || layout === "auto") {
    railStyle.display = layout === "grid" ? "grid" : undefined;
    // Auto stays as snap-rail on mobile, becomes grid on desktop via CSS class.
    if (layout === "grid" || (layout === "auto" && isDesktopVP)) {
      railStyle.gridTemplateColumns = `repeat(${perRow}, minmax(0, 1fr))`;
    }
  }
  railStyle.gap = gapPx > 0 ? `${gapPx}px` : undefined;

  // Auto-advance for carousel mode — 3.5 s per tick, pauses on hover.
  useEffect(() => {
    if (layout !== "carousel") return;
    const el = railRef.current;
    if (!el) return;
    let paused = false;
    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    const iv = setInterval(() => {
      if (paused || !el) return;
      const step = Math.max(80, el.clientWidth * 0.6);
      if (el.scrollLeft + el.clientWidth + 8 >= el.scrollWidth) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: step, behavior: "smooth" });
      }
    }, 3500);
    return () => { clearInterval(iv); el.removeEventListener("mouseenter", onEnter); el.removeEventListener("mouseleave", onLeave); };
  }, [layout]);

  const isVertical = layout === "vertical";
  // Inline style for the tile (box). We prefer inline styles over Tailwind
  // arbitrary classes so any admin value works without a Tailwind rebuild.
  const tileStyle = (i) => {
    const s = {};
    if (boxSize > 0)               { s.width = `${boxSize}px`; s.height = `${boxSize}px`; }
    if (boxPad > 0)                s.padding = `${boxPad}px`;
    if (radiusPx > 0)              s.borderRadius = `${radiusPx}px`;
    if (bWidth > 0)                { s.borderWidth = `${bWidth}px`; s.borderStyle = "solid"; }
    if (bColor)                    s.borderColor = bColor;
    if (boxBg)                     s.backgroundColor = boxBg;
    return s;
  };
  const imgWrapStyle = () => {
    const s = {};
    if (imgPct && imgPct !== 100)  { s.width = `${imgPct}%`; s.height = `${imgPct}%`; }
    if (imgBg)                     s.backgroundColor = imgBg;
    return s;
  };
  const labelStyle = () => {
    const s = {};
    if (labelWeight)               s.fontWeight = labelWeight;
    if (labelSizePx)               s.fontSize = `${labelSizePx}px`;
    if (labelColor)                s.color = labelColor;
    return s;
  };
  const imgFitCls =
    imgFit === "contain" ? "object-contain" :
    imgFit === "fill"    ? "object-fill"    :
    imgFit === "none"    ? "object-none"    :
    /* cover (default) */  "object-cover";

  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-3 pb-1" data-testid="category-rail">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-base md:text-lg font-display font-semibold">Shop by category</h2>
        <Link to="/category" className="text-xs md:text-sm text-primary hover:underline font-semibold">See all →</Link>
      </div>
      <div ref={railRef} className={railCls} style={railStyle} data-testid={`category-rail-${layout}`}>
        {list.slice(0, 20).map((c, i) => {
          const isReal = !!c.id;
          const to = isReal ? `/category?category=${c.id}` : `/shop?q=${encodeURIComponent(c.label || c.key)}`;
          const bgFallback = isReal ? TILE_BGS[i % TILE_BGS.length] : c.bg;
          const label = isReal ? c.name : c.label;
          const useCustomBg = !!boxBg;   // if admin set a solid color, don't apply the pastel utility class
          return (
            <Link
              key={isReal ? c.id : c.key}
              to={to}
              className={`${isVertical ? "flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-secondary/50" : "snap-start shrink-0 w-[70px] md:w-auto flex flex-col items-center gap-1"} group`}
              data-testid={`cat-tile-${i}`}
            >
              <div
                className={`${isVertical ? "w-11 h-11 shrink-0" : (boxSize ? "" : "w-[62px] h-[62px] md:w-full md:aspect-square")} ${radiusPx > 0 ? "" : shape} overflow-hidden ${useCustomBg ? "" : bgFallback} grid place-items-center ${shadowCls} group-hover:-translate-y-0.5 transition`}
                style={tileStyle(i)}
              >
                <div
                  className={`${radiusPx > 0 ? "" : shape} overflow-hidden grid place-items-center`}
                  style={{ width: imgPct === 100 ? "100%" : `${imgPct}%`, height: imgPct === 100 ? "100%" : `${imgPct}%`, ...imgWrapStyle() }}
                >
                  {isReal && c.image_url
                    ? <img src={c.image_url} alt={c.name} className={`w-full h-full ${imgFitCls}`} loading="lazy" />
                    : <div className={isVertical ? "text-lg" : "text-2xl md:text-4xl"}>{isReal ? emojiFor(c.name) : c.emoji}</div>}
                </div>
              </div>
              <div
                className={`${isVertical ? "text-sm text-left" : "text-[10.5px] md:text-[11px] leading-tight line-clamp-2 text-center px-0.5"} text-foreground/85 group-hover:text-primary transition ${fontCls}`}
                style={labelStyle()}
              >
                {label}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function useIsDesktop() {
  const [d, setD] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(min-width: 768px)").matches
      : true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const on = (e) => setD(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return d;
}

function ProductRail({ title, subtitle, products, testId, badge, perRowMobile, perRowDesktop, cfg }) {
  const isDesktop = useIsDesktop();
  const railRef = useRef(null);

  // Layout mode — new Visual Builder writes cfg.layout on each homepage
  // section: auto | grid | horizontal | vertical | slider | auto_carousel.
  // Legacy behaviour: no layout key → fall back to Blinkit-style rail
  // + optional cols (perRowMobile/perRowDesktop) — 100% backward compat.
  const layout = cfg?.layout || null;
  const cardOverride = cfg?.card_override || null;
  const gap = Number(cfg?.gap ?? 0);
  const autoplay = !!cfg?.autoplay;
  const autoplayInterval = Number(cfg?.autoplay_interval || 3500);
  const showArrows = cfg?.show_arrows !== false;

  // Auto-carousel — scroll horizontally on an interval, pauses on hover.
  // Declared BEFORE any early return so the hook order stays stable.
  useEffect(() => {
    if (layout !== "auto_carousel" && !(layout === "slider" && autoplay)) return;
    const el = railRef.current;
    if (!el) return;
    let paused = false;
    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    const iv = setInterval(() => {
      if (paused || !el) return;
      const step = Math.max(120, el.clientWidth * 0.5);
      if (el.scrollLeft + el.clientWidth + 8 >= el.scrollWidth) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: step, behavior: "smooth" });
      }
    }, autoplayInterval);
    return () => { clearInterval(iv); el.removeEventListener("mouseenter", onEnter); el.removeEventListener("mouseleave", onLeave); };
  }, [layout, autoplay, autoplayInterval]);

  if (products.length === 0) return null;

  // Responsive cols per breakpoint (new schema) — fall back to legacy
  // per_row_mobile / per_row_desktop when not set.
  const cols = (() => {
    if (layout === "grid" || layout === "auto" || layout === "vertical") {
      const desk = Number(cfg?.desktop_cols) || perRowDesktop;
      const mob  = Number(cfg?.mobile_cols)  || perRowMobile;
      const dCols = desk && desk > 0 ? desk : 5;
      const mCols = mob  && mob  > 0 ? mob  : 2;
      return isDesktop ? dCols : mCols;
    }
    return null;
  })();

  const scrollBy = (dir) => {
    const el = railRef.current; if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: "smooth" });
  };

  // Compute container class + inline style per layout mode.
  const useCustomGrid = cols != null;
  let containerCls = "";
  let containerStyle = {};
  const gapStyle = gap > 0 ? { gap: `${gap}px` } : {};

  if (layout === "grid") {
    containerCls = "grid";
    containerStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, ...gapStyle };
  } else if (layout === "vertical") {
    containerCls = "grid grid-cols-1";
    containerStyle = { ...gapStyle };
  } else if (layout === "horizontal" || layout === "slider" || layout === "auto_carousel") {
    containerCls = "snap-rail rail-fade-mask pb-1";
    containerStyle = { ...gapStyle };
  } else if (layout === "auto") {
    containerCls = "snap-rail rail-fade-mask md:mask-none md:grid pb-1";
    if (isDesktop) containerStyle = { display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, ...gapStyle };
    else containerStyle = { ...gapStyle };
  } else if (useCustomGrid || (perRowMobile && perRowMobile > 0) || (perRowDesktop && perRowDesktop > 0)) {
    // Legacy custom cols path — preserved.
    const legacyMob  = perRowMobile  && perRowMobile  > 0 ? perRowMobile  : 2;
    const legacyDesk = perRowDesktop && perRowDesktop > 0 ? perRowDesktop : 5;
    const c = isDesktop ? legacyDesk : legacyMob;
    containerCls = "grid gap-2 md:gap-4";
    containerStyle = { gridTemplateColumns: `repeat(${c}, minmax(0,1fr))` };
  } else {
    // Legacy default rail — Blinkit-style horizontal.
    containerCls = "snap-rail rail-fade-mask gap-2 md:gap-4 md:grid md:grid-cols-5";
  }

  const useRail = layout === "horizontal" || layout === "slider" || layout === "auto_carousel" || (!layout && !cols);
  const itemWrapCls = useRail
    ? "snap-start shrink-0 w-[32vw] max-w-[150px] md:w-auto md:max-w-none"
    : undefined;

  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-3 pb-1" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {badge && <span className="flash-pill" data-testid={`${testId}-badge`}>{badge}</span>}
            <h2 className="text-base md:text-lg font-display font-semibold truncate">{title}</h2>
          </div>
          {subtitle && <p className="text-[11px] md:text-xs text-muted-foreground mt-0.5 hidden md:block">{subtitle}</p>}
        </div>
        <Link to="/shop" className="text-xs md:text-sm text-primary hover:underline shrink-0 font-semibold">See all →</Link>
      </div>

      <div className="relative">
        {useRail && showArrows && (
          <>
            <button
              type="button" onClick={() => scrollBy(-1)}
              className="hidden md:grid absolute -left-3 top-1/2 -translate-y-1/2 w-8 h-8 place-items-center rounded-full bg-white/95 shadow-md border border-slate-200 hover:bg-white z-10"
              aria-label="Prev" data-testid={`${testId}-arrow-prev`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button" onClick={() => scrollBy(1)}
              className="hidden md:grid absolute -right-3 top-1/2 -translate-y-1/2 w-8 h-8 place-items-center rounded-full bg-white/95 shadow-md border border-slate-200 hover:bg-white z-10"
              aria-label="Next" data-testid={`${testId}-arrow-next`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        <div
          ref={railRef}
          className={containerCls}
          style={containerStyle}
          data-testid={`${testId}-grid`}
          data-layout={layout || "legacy"}
          data-cols={cols || undefined}
        >
          {products.map((p) => (
            <div key={p.id} className={itemWrapCls} data-testid={`${testId}-card-${p.id}`}>
              <ProductCard p={p} cardOverride={cardOverride} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OfferStrip({ banners }) {
  if (banners.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-4" data-testid="offer-strip">
      <div className={`grid gap-3 ${banners.length >= 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
        {banners.slice(0, 4).map((b, i) => (
          <Link
            key={i}
            to={b.link_to || "/shop"}
            className="relative rounded-xl overflow-hidden aspect-[16/5] group fade-in-up"
            style={{ animationDelay: `${i * 60}ms` }}
            data-testid={`offer-banner-${i}`}
          >
            <img src={b.image} alt={b.title || ""} className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-500" />
            {(b.title || b.subtitle) && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent flex flex-col justify-end p-4 text-white">
                {b.title && <div className="font-display text-lg md:text-xl font-semibold">{b.title}</div>}
                {b.subtitle && <div className="text-xs opacity-90 mt-0.5">{b.subtitle}</div>}
              </div>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}

function AppBannerSection({ banners }) {
  if (banners.length === 0) return null;
  const b = banners[0];
  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-4" data-testid="app-banner">
      <Link
        to={b.link_to || "#"}
        className="relative grid md:grid-cols-[1fr_260px] gap-3 rounded-2xl overflow-hidden border bg-gradient-to-r from-primary/95 to-[hsl(var(--brand-red-deep))] text-primary-foreground p-5 md:p-8 group"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider opacity-80">Babu Baazar App</div>
          <div className="mt-1.5 text-xl md:text-3xl font-display font-semibold">{b.title || "Get the app"}</div>
          <p className="mt-1.5 text-xs md:text-sm opacity-90 max-w-md">{b.subtitle || "Faster checkout, order tracking, and exclusive app-only deals."}</p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-primary px-3.5 py-1 text-xs md:text-sm font-semibold shadow-sm">Download now</div>
        </div>
        <div className="relative shrink-0 h-28 md:h-40">
          <img src={b.image} alt={b.title || "App"} className="absolute inset-0 w-full h-full object-contain md:object-cover group-hover:scale-[1.03] transition duration-500" />
        </div>
      </Link>
    </section>
  );
}

function PopupBanner({ banners }) {
  const [visible, setVisible] = useState(false);
  const banner = banners[0];
  useEffect(() => {
    if (!banner) return;
    let dismissed = "";
    try { dismissed = sessionStorage.getItem("bb_popup_dismissed_v1") || ""; } catch { /* noop */ }
    if (dismissed === banner.title) return;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [banner]);
  if (!banner || !visible) return null;
  const close = () => {
    setVisible(false);
    try { sessionStorage.setItem("bb_popup_dismissed_v1", banner.title || ""); } catch { /* noop */ }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 fade-in-up" onClick={close} data-testid="popup-banner">
      <div className="relative bg-card rounded-3xl overflow-hidden max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-white/85 backdrop-blur shadow hover:bg-white z-10" data-testid="popup-close">
          <ChevronRight className="w-4 h-4 rotate-45" />
        </button>
        <Link to={banner.link_to || "/shop"} onClick={close}>
          <img src={banner.image} alt={banner.title || ""} className="w-full aspect-square object-cover" />
          {(banner.title || banner.subtitle) && (
            <div className="p-5">
              {banner.title && <div className="font-display text-xl font-semibold">{banner.title}</div>}
              {banner.subtitle && <div className="text-sm text-muted-foreground mt-1">{banner.subtitle}</div>}
            </div>
          )}
        </Link>
      </div>
    </div>
  );
}

function AllProductsGrid({ products, perRowMobile, perRowDesktop, cfg }) {
  const [visibleCount, setVisibleCount] = useState(24);
  const isDesktop = useIsDesktop();
  if (products.length === 0) return null;
  const shown = products.slice(0, visibleCount);
  const hasMore = visibleCount < products.length;
  const cardOverride = cfg?.card_override || null;
  const useCustom = (perRowMobile && perRowMobile > 0) || (perRowDesktop && perRowDesktop > 0);
  const mCols = perRowMobile && perRowMobile > 0 ? perRowMobile : 2;
  const dCols = perRowDesktop && perRowDesktop > 0 ? perRowDesktop : 5;
  const cols = isDesktop ? dCols : mCols;
  return (
    <section className="max-w-7xl mx-auto px-3 md:px-6 pt-4 pb-2" data-testid="all-products">
      <div className="flex items-baseline justify-between mb-2">
        <div className="min-w-0">
          <h2 className="text-base md:text-lg font-display font-semibold">All products</h2>
          <p className="text-[11px] md:text-xs text-muted-foreground mt-0.5">
            Browse every item in your Babu Store — <span className="font-semibold text-primary tabular-nums">{products.length}</span> live
          </p>
        </div>
        <Link to="/shop" className="text-xs md:text-sm text-primary hover:underline shrink-0 font-semibold">
          View all →
        </Link>
      </div>
      <div
        className={useCustom ? "grid gap-2 md:gap-4" : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-4"}
        style={useCustom ? { gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` } : undefined}
        data-testid="all-products-grid"
        data-per-row-mobile={useCustom ? mCols : undefined}
        data-per-row-desktop={useCustom ? dCols : undefined}
      >
        {shown.map((p) => (
          <div key={p.id} data-testid={`all-products-card-${p.id}`}>
            <ProductCard p={p} cardOverride={cardOverride} />
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => setVisibleCount((c) => Math.min(products.length, c + 24))}
            className="rounded-full h-9 px-5 text-sm font-semibold"
            data-testid="all-products-load-more"
          >
            Load more products ({products.length - visibleCount} remaining)
          </Button>
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { homepageSections, homepageSeo, homepageGrid, ui } = useStore();
  const vbTree = Array.isArray(ui?.tree) ? ui.tree : [];
  const { ids: recentIds } = useRecentlyViewed();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [homeSlider, setHomeSlider] = useState([]);
  const [offerBanners, setOfferBanners] = useState([]);
  const [festivalBanners, setFestivalBanners] = useState([]);
  const [brandBanners, setBrandBanners] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const [bottomBanners, setBottomBanners] = useState([]);
  const [appBanners, setAppBanners] = useState([]);
  const [popupBanners, setPopupBanners] = useState([]);
  const [buyAgain, setBuyAgain] = useState([]);
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        // Sprint — Guest browsing: products/categories are public; only fetch
        // the "Buy again" order history when the shopper is signed in.
        const publicSettingsCall = user
          ? api.get("/store/settings").catch(() => ({ data: {} }))
          : api.get("/store/settings/public").catch(() => ({ data: {} }));
        const ordersCall = user
          ? api.get("/orders?limit=6").catch(() => ({ data: { items: [] } }))
          : Promise.resolve({ data: { items: [] } });
        const [p, c, s, o] = await Promise.all([
          // Sprint 74 — fetch full catalog (up to 300 products) via parallel pages so
          // the homepage can display the complete babustore inventory, not just a slice.
          (async () => {
            const first = await api.get("/products?page=1&limit=100");
            const total = first.data?.total || (first.data?.items || []).length;
            const pages = Math.min(3, Math.ceil(total / 100)); // cap at 300 for perf
            if (pages <= 1) return first;
            const rest = await Promise.all(
              Array.from({ length: pages - 1 }).map((_, i) =>
                api.get(`/products?page=${i + 2}&limit=100`).then((r) => r.data?.items || []).catch(() => []),
              ),
            );
            const merged = [...(first.data?.items || []), ...rest.flat()];
            return { data: { ...first.data, items: merged } };
          })(),
          api.get("/categories?limit=100"),
          publicSettingsCall,
          ordersCall,
        ]);
        setProducts((p.data.items || []).filter((x) => !/^TEST[_-]/i.test(x.name || "")));
        setCats((c.data.items || [])
          .filter((x) => x.is_active !== false)
          .filter((x) => !x.parent_id)                         // Sprint 14 #4 — parent categories only on Home
          .filter((x) => !/^TEST[_-]/i.test(x.name || ""))
          .slice(0, 12));
        // Public endpoint returns `marketing` at the top level; authed
        // /store/settings returns it nested under `settings`. Support both.
        const mkt = s.data?.settings?.marketing || s.data?.marketing || {};
        // Sprint 62 — accept legacy banners: `image` OR `image_url`; treat missing
        // is_active as active (many admin uploads omit it). Filter still removes
        // explicit is_active:false so admin can turn banners off.
        const alive = (arr) => (Array.isArray(arr) ? arr : []).filter((b) => (b.image || b.image_url) && b.is_active !== false);
        setHomeSlider(alive(mkt.home_slider));
        setOfferBanners(alive(mkt.offer_banner));
        setFestivalBanners(alive(mkt.festival_banner));
        setBrandBanners(alive(mkt.brand_banner));
        setTopBanners(alive(mkt.top_banner));
        setMiddleBanners(alive(mkt.middle_banner));
        setBottomBanners(alive(mkt.bottom_banner));
        setAppBanners(alive(mkt.app_banner));
        setPopupBanners(alive(mkt.popup_banner));

        // Buy again — unique products from recent orders
        const seen = new Map();
        (o.data.items || []).forEach((ord) => (ord.items || []).forEach((it) => {
          if (it.product_id && !seen.has(it.product_id)) seen.set(it.product_id, it);
        }));
        const buyAgainIds = Array.from(seen.keys()).slice(0, 8);
        if (buyAgainIds.length > 0) {
          const catalog = new Map(p.data.items?.map((x) => [x.id, x]));
          const missing = buyAgainIds.filter((id) => !catalog.has(id));
          const hydrated = await Promise.all(missing.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null)));
          hydrated.filter(Boolean).forEach((row) => catalog.set(row.id, row));
          setBuyAgain(buyAgainIds.map((id) => catalog.get(id)).filter(Boolean));
        }
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, [user]);

  // Recently-viewed hydration (works for guests too — /products/:id is public).
  useEffect(() => {
    if (recentIds.length === 0) { setRecentlyViewedProducts([]); return; }
    let cancelled = false;
    (async () => {
      const rows = await Promise.all(recentIds.slice(0, 12).map((id) =>
        api.get(`/products/${id}`).then((r) => r.data).catch(() => null)));
      if (!cancelled) setRecentlyViewedProducts(rows.filter(Boolean));
    })();
    return () => { cancelled = true; };
  }, [recentIds]);

  // Apply homepage SEO (title / description / keywords) to <head>. Owned by
  // the Website Builder → homepage.seo. Restores the original title on
  // unmount so other routes aren't polluted.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevTitle = document.title;
    const setMeta = (name, content) => {
      if (!content) return null;
      let el = document.querySelector(`meta[name="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prevContent = el.getAttribute("content");
      el.setAttribute("content", content);
      return () => {
        if (created) el.remove();
        else if (prevContent != null) el.setAttribute("content", prevContent);
      };
    };
    if (homepageSeo?.title) document.title = homepageSeo.title;
    const undoDesc = setMeta("description", homepageSeo?.description);
    const undoKw = setMeta("keywords", homepageSeo?.keywords);
    return () => {
      document.title = prevTitle;
      undoDesc?.();
      undoKw?.();
    };
  }, [homepageSeo?.title, homepageSeo?.description, homepageSeo?.keywords]);

  const flashDeals = useMemo(() => {
    // Sprint 28 — prefer admin-flagged hot deals; fallback to discount-sorted list
    const tagged = products.filter((p) => p.hot_deal);
    if (tagged.length > 0) {
      return tagged.map((p) => ({
        ...p,
        _disc: p.mrp && Number(p.mrp) > Number(p.selling_price)
          ? Math.round(((p.mrp - p.selling_price) / p.mrp) * 100) : 0,
      })).slice(0, 12);
    }
    return products
      .filter((p) => p.mrp && Number(p.mrp) > Number(p.selling_price))
      .map((p) => ({ ...p, _disc: Math.round(((p.mrp - p.selling_price) / p.mrp) * 100) }))
      .sort((a, b) => b._disc - a._disc)
      .slice(0, 8);
  }, [products]);

  const trending = useMemo(() => {
    // Sprint 28 — admin-tagged trending products; fallback to first 8
    const tagged = products.filter((p) => p.trending);
    return (tagged.length > 0 ? tagged : products).slice(0, 12);
  }, [products]);
  const recommended = products.slice(8, 20);
  const bestSellers = useMemo(() => {
    // Sprint 28 — admin-flagged best sellers; fallback to slice
    const tagged = products.filter((p) => p.best_seller);
    if (tagged.length > 0) return tagged.slice(0, 12);
    const arr = products.filter((p) => (p.tags || []).some((t) => /best.?seller/i.test(String(t))) || p.is_best_seller);
    return (arr.length > 0 ? arr : products.slice(20, 32)).slice(0, 8);
  }, [products]);

  // Section registry — matches keys in DEFAULT_HOMEPAGE_SECTIONS.
  // Each key holds a function that returns JSX given the saved section
  // config (so per-section overrides like `per_row_mobile/desktop` reach
  // the rendered rail).
  //
  // Sprint 79 — Global Products-Per-Row + per-section Total Limit + Content
  // Filter (categories/brands). Global is the master: whenever a global
  // value is set, EVERY product-grid section uses it. Per-section legacy
  // `per_row_*` values only apply when the global one is unset (0).
  const pickCfg = (cfg) => {
    const mob = Number(homepageGrid?.per_row_mobile) || Number(cfg?.per_row_mobile) || 0;
    const desk = Number(homepageGrid?.per_row_desktop) || Number(cfg?.per_row_desktop) || 0;
    return { mob, desk };
  };
  const applyFilterAndLimit = (list, cfg) => {
    let out = list;
    // Content Filter — narrow by selected category IDs or brand IDs (either
    // one is enough to match; empty arrays mean "no filter"). We compare on
    // both `category_id`/`brand_id` and the older `category`/`brand` slug
    // shapes so legacy products still match.
    const catIds = (cfg?.filter_categories || []).filter(Boolean);
    const brandIds = (cfg?.filter_brands || []).filter(Boolean);
    if (catIds.length || brandIds.length) {
      out = out.filter((p) => {
        const catHit = catIds.length ? (catIds.includes(p.category_id) || catIds.includes(p.category)) : true;
        const brandHit = brandIds.length ? (brandIds.includes(p.brand_id) || brandIds.includes(p.brand)) : true;
        return catHit && brandHit;
      });
    }
    // Total Product Limit — 0 / null / undefined means "show all".
    const lim = Number(cfg?.limit) || 0;
    if (lim > 0) out = out.slice(0, lim);
    return out;
  };
  const railProps = (products, cfg) => {
    const { mob, desk } = pickCfg(cfg);
    return { products: applyFilterAndLimit(products, cfg), perRowMobile: mob, perRowDesktop: desk, cfg };
  };

  const registry = {
    hero:            () => (homeSlider.length > 0 ? <HeroSlider banners={homeSlider} /> : <DefaultHeroBanner user={user} />),
    trust_strip:     () => <TrustStrip />,
    categories:      () => <CategoryRail cats={cats} />,
    flash_deals:     (cfg) => <ProductRail title="Hot deals" subtitle="Biggest savings, ending soon" testId="flash-deals" badge={<span className="inline-flex items-center gap-1"><Flame className="w-3 h-3" />HOT</span>} {...railProps(flashDeals, cfg)} />,
    festival_banner: () => <OfferStrip banners={festivalBanners} />,
    trending:        (cfg) => <ProductRail title="Trending now" subtitle="What your neighbours are buying" testId="trending" {...railProps(trending, cfg)} />,
    middle_banner:   () => <OfferStrip banners={middleBanners} />,
    recently_viewed: (cfg) => <ProductRail title="Recently viewed" subtitle="Pick up where you left off" testId="recently-viewed" {...railProps(recentlyViewedProducts, cfg)} />,
    buy_again:       (cfg) => <ProductRail title="Buy again" subtitle="Your usual essentials" testId="buy-again" {...railProps(buyAgain, cfg)} />,
    offer_banner:    () => <OfferStrip banners={offerBanners} />,
    best_sellers:    (cfg) => <ProductRail title="Best sellers" subtitle="Community favourites" testId="best-sellers" {...railProps(bestSellers, cfg)} />,
    recommended:     (cfg) => <ProductRail title="Recommended for you" subtitle="Curated from your Babu Store" testId="recommended" {...railProps(recommended, cfg)} />,
    brand_banner:    () => <OfferStrip banners={brandBanners} />,
    bottom_banner:   () => <OfferStrip banners={bottomBanners} />,
    all_products:    (cfg) => <AllProductsGrid {...railProps(products, cfg)} />,
    app_banner:      () => <AppBannerSection banners={appBanners} />,
    offer_cta:       () => (
      <section className="max-w-7xl mx-auto px-3 md:px-6 pt-4">
        <div className="rounded-2xl bg-primary text-primary-foreground p-5 md:p-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "repeating-linear-gradient(45deg, white 0, white 1px, transparent 1px, transparent 12px)",
          }} />
          <div className="relative grid md:grid-cols-2 gap-4 items-center">
            <div>
              <Percent className="w-6 h-6 mb-2" />
              <div className="text-xl md:text-3xl font-display font-semibold">Free delivery over ₹500</div>
              <p className="mt-1 text-primary-foreground/85 max-w-md text-xs md:text-sm">Stock up your monthly kitchen — bigger baskets, bigger savings.</p>
              <Button asChild size="sm" variant="secondary" className="mt-3 bg-white text-primary hover:bg-white/90 h-9 rounded-lg" data-testid="offer-shop-btn">
                <Link to="/shop">Start shopping</Link>
              </Button>
            </div>
            <div className="hidden md:flex justify-end">
              <div className="w-28 h-28 rounded-full bg-white/15 backdrop-blur grid place-items-center">
                <ShoppingBag className="w-12 h-12 text-white/70" />
              </div>
            </div>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <div data-testid="home-page" className="pb-16 md:pb-8">
      {/* Sprint 58 — Delivery bar now lives in CustomerHeader (above search on mobile).
          Removed duplicate DeliveryStrip from Home. */}

      {/* Top banner (marketing) */}
      <OfferStrip banners={topBanners} />

      {/* Sprint — Guest browsing: no more sign-in gate on the homepage.
          The full product/category/marketing experience is public. Sign-in is
          only required at the /checkout step. */}

      {loading && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card-soft border-transparent overflow-hidden">
                <Skeleton className="aspect-square w-full" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!loading && vbTree.length > 0 && (
        <div className="pt-4">
          <TreeRenderer tree={vbTree} />
        </div>
      )}

      {!loading && homepageSections
        .filter((s) => s.enabled !== false)
        .map((s) => {
          const factory = registry[s.key];
          if (typeof factory !== "function") return null;
          const node = factory(s);
          if (!node) return null;
          return <div key={s.key}>{node}</div>;
        })}

      {/* Popup banner — first-visit modal, session-dismissible */}
      {!loading && <PopupBanner banners={popupBanners} />}
    </div>
  );
}
